import { Link } from "react-router-dom";
import { JobWorkspaceIcon } from "./icons";
import { WorkspaceButton, WorkspaceCard } from "./ui";

/**
 * Loads and legs table for one job.
 * @param {{
 *   view: object,
 *   canAddLeg?: boolean,
 *   onAddLeg?: () => void,
 *   onSelectLeg?: (leg: object) => void,
 *   selectedLegId?: string,
 *   jobPath?: string,
 * }} props
 * @returns {JSX.Element}
 */
export function LoadsAndLegs({
  view,
  canAddLeg = false,
  onAddLeg,
  onSelectLeg,
  selectedLegId,
  jobPath,
}) {
  return (
    <WorkspaceCard>
      <div className="flex items-center justify-between p-5">
        <div className="flex items-center gap-2">
          <h2 className="text-[16px] font-semibold leading-normal text-[#202b33]">
            {view.legsTitle}
          </h2>
          <span className="rounded-[5px] bg-[#f7f9fa] px-[7px] py-[3px] text-[11px] font-semibold leading-normal text-[#6e7b84]">
            {view.legs.length}
          </span>
        </div>
        <WorkspaceButton
          className="h-8"
          disabled={!canAddLeg}
          onClick={onAddLeg}
          icon={<JobWorkspaceIcon name="plus" />}
        >
          Add Leg
        </WorkspaceButton>
      </div>
      <div className="overflow-x-auto">
      <div className="flex h-9 min-w-[720px] items-center gap-4 border-y border-[#e2e7ea] bg-[#f7f9fa] px-5">
        <p className="w-[184px] shrink-0 text-[10px] font-semibold leading-normal text-[#6e7b84]">
          LEG / LOAD
        </p>
        <p className="min-w-0 flex-1 text-[10px] font-semibold leading-normal text-[#6e7b84]">
          ROUTE
        </p>
        <p className="w-[116px] shrink-0 text-[10px] font-semibold leading-normal text-[#6e7b84]">
          DRIVER
        </p>
        <p className="w-[100px] shrink-0 text-[10px] font-semibold leading-normal text-[#6e7b84]">
          LEG STATUS
        </p>
        <span className="w-[15px] shrink-0" />
      </div>
      {view.legs.length === 0 ? (
        <p className="px-5 py-4 text-[12px] leading-normal text-[#6e7b84]">
          No legs on this job.
        </p>
      ) : (
        view.legs.map((leg) => {
          const row = (
            <>
              <span className="flex w-[184px] shrink-0 items-center gap-[10px]">
                <span className="flex h-8 w-[30px] items-center justify-center rounded-[5px] bg-[#f7f9fa]">
                  <JobWorkspaceIcon name="route" />
                </span>
                <span className="flex min-w-0 flex-col gap-[6px]">
                  <span className="truncate text-[12px] font-semibold leading-normal text-[#202b33]">
                    {leg.label}
                  </span>
                  <span className="text-[11px] font-normal leading-normal text-[#6e7b84]">
                    {leg.loadNumber}
                  </span>
                </span>
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-[6px]">
                <span className="truncate text-[12px] font-normal leading-normal text-[#6e7b84]">
                  {leg.route}
                </span>
                {leg.routeDetail ? (
                  <span className="text-[10px] font-normal leading-normal text-[#909ba3]">
                    {leg.routeDetail}
                  </span>
                ) : null}
              </span>
              <span className="w-[116px] shrink-0 truncate text-[12px] font-normal leading-normal text-[#6e7b84]">
                {leg.driver}
              </span>
              <span className="w-[100px] shrink-0 truncate text-[12px] font-normal leading-normal text-[#6e7b84]">
                {leg.status}
              </span>
              <JobWorkspaceIcon name="chevronRight15" />
            </>
          );

          const selected = selectedLegId && selectedLegId === leg.id;
          const className = `flex h-[88px] min-w-[720px] items-center gap-4 border-b border-[#e2e7ea] px-5 text-left hover:bg-[#f7f9fa] ${
            selected ? "bg-[#f7f9fa]" : ""
          }`;

          if (onSelectLeg) {
            return (
              <button
                key={leg.id}
                type="button"
                aria-pressed={Boolean(selected)}
                onClick={() => onSelectLeg(leg)}
                className={className}
              >
                {row}
              </button>
            );
          }

          return jobPath ? (
            <Link key={leg.id} to={jobPath} className={className}>
              {row}
            </Link>
          ) : (
            <div key={leg.id} className={className}>
              {row}
            </div>
          );
        })
      )}
      </div>
      {view.unresolvedCopy ? (
        <div className="flex items-start gap-2 px-5 py-[14px]">
          <JobWorkspaceIcon name="info" />
          <p className="text-[11px] font-normal leading-[1.5] text-[#6e7b84]">
            {view.unresolvedCopy}
          </p>
        </div>
      ) : null}
    </WorkspaceCard>
  );
}
